import React from "react";
import PropTypes from "prop-types";
import Clickable from "./Clickable";
import UnderlineInput from "./UnderlineInput";
import { GradientLabel } from "./PageShell";
import { documentIcon, documentLabel, formatDate } from "./fileMeta";

const money = (v) => (Number(v) || 0).toLocaleString("en-US");
const TH = "tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3 tw-px-3 tw-py-2.5 tw-text-left tw-whitespace-nowrap";

// Chi tiết phụ hiện thành dòng nhỏ dưới tên tài liệu (trước là một cột riêng, rất
// chiếm chỗ). Bỏ số hóa đơn nếu tên đã chính là số hóa đơn.
const documentDetails = (doc) => (
  [doc.inv_no && doc.inv_no !== documentLabel(doc) && `#${doc.inv_no}`, formatDate(doc.inv_date), doc.vendor_name]
).filter(Boolean).join(" · ");

/**
 * Bảng hóa đơn Doc Search của phiếu (thêm qua InvoiceSearchDrawer) — mỗi hóa đơn
 * đồng thời sinh một dòng chi phí. File người lập tự tải lên nằm riêng ở
 * AttachmentsPanel (dưới ô Note), không hiện ở đây.
 *
 * Props:
 *   documents      Array — dòng doc_search; `_idx` = vị trí trong documents_records
 *                  (bảng chỉ hiện một phần mảng, nên register/onRemove phải dùng _idx)
 *   readOnly       bool  — trang chi tiết dùng chế độ này (không thêm/xóa/sửa)
 *   activeId       string|null — document_id đang preview (highlight)
 *   onPreview(doc) — bấm vào dòng
 *   onRemove(idx, doc)
 *   onSearch()     — mở drawer doc-search
 *   register       — RHF register (bỏ qua khi readOnly) để sửa ghi chú tại chỗ
 */
const DocumentsTable = ({
  documents = [],
  readOnly = false,
  activeId = null,
  onPreview,
  onRemove,
  onSearch,
  onErpSearch,
  register,
}) => {

  return (
    <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden tw-flex-shrink-0">
      <div className="tw-px-5 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
        <div className="tw-flex tw-items-baseline tw-gap-2">
          <GradientLabel className="tw-mb-0 tw-text-sm">Invoices</GradientLabel>
          <span className="tw-text-xs tw-text-geist-fg-3">{documents.length}</span>
        </div>

        {!readOnly && (
          <div className="tw-flex tw-items-center tw-gap-3">
            <Clickable onClick={onSearch}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100">
              <i className="bi bi-search" /> Find invoice
            </Clickable>
            {onErpSearch && (
              <Clickable onClick={onErpSearch} title="Vendor invoices already posted in AX — paid with journal APP"
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100">
                <i className="bi bi-database" /> From PO ERP
              </Clickable>
            )}
          </div>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="tw-px-5 tw-py-8 tw-text-center">
          <span className="tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-mb-3">
            <i className="bi bi-receipt" style={{ fontSize: 18 }} />
          </span>
          <p className="tw-text-sm tw-font-semibold tw-text-geist-fg tw-mb-1">No invoice yet</p>
          <p className="tw-text-[13px] tw-text-geist-fg-3 tw-m-0">
            {readOnly ? "No invoice on this request." : "Find an invoice in Doc Search, or take a posted invoice from ERP. Other files go under Attachments below."}
          </p>
        </div>
      ) : (
        <div className="tw-overflow-x-auto tw-px-3">
          <table className="tw-w-full tw-border-collapse">
            <thead>
              <tr className="tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
                <th className={`${TH} tw-w-8`}>#</th>
                <th className={TH}>Document</th>
                <th className={`${TH} tw-text-right tw-w-28`}>Amount</th>
                <th className={TH}>Note</th>
                <th className={`${TH} tw-w-20 tw-text-right`} />
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, idx) => {
                const docId = doc.document_id || doc.doc_platform_document_id;
                const active = docId && activeId === docId;
                // Invoices from ERP are posted in AX — no file to preview.
                const previewable = !!docId && doc.source !== "erp_invoice";
                const details = documentDetails(doc);
                const rowIdx = doc._idx ?? idx;
                return (
                  <tr key={doc._key || doc.id || docId || idx}
                    onClick={() => previewable && onPreview?.(doc)}
                    className={`tw-border-0 tw-border-b tw-border-solid tw-border-geist-border last:tw-border-b-0 ${previewable ? "tw-cursor-pointer" : ""} ${active ? "tw-bg-geist-100" : "hover:tw-bg-geist-bg-2"}`}>
                    <td className="tw-px-3 tw-py-2 tw-h-11 tw-text-sm tw-text-geist-fg-3 tw-tabular-nums">{idx + 1}</td>

                    <td className="tw-px-3 tw-py-2 tw-max-w-0 tw-w-full">
                      <div className="tw-flex tw-items-center tw-gap-2.5 tw-min-w-0">
                        <span className="tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-geist-sm tw-bg-geist-100 tw-text-geist-accent tw-flex-shrink-0">
                          <i className={`bi ${documentIcon(doc)} tw-text-[12px]`} />
                        </span>
                        <div className="tw-min-w-0" title={[documentLabel(doc), details].filter(Boolean).join("\n")}>
                          <div className="tw-text-sm tw-font-medium tw-text-geist-fg tw-truncate tw-leading-5">{documentLabel(doc)}</div>
                          {details && <div className="tw-text-[11px] tw-text-geist-fg-3 tw-truncate tw-leading-4">{details}</div>}
                        </div>
                      </div>
                    </td>

                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-base tw-font-semibold tw-tabular-nums tw-text-primary tw-whitespace-nowrap">
                      {doc.amount ? money(doc.amount) : ""}
                    </td>

                    <td className="tw-px-3 tw-py-2 tw-w-40 tw-min-w-[120px]" onClick={(e) => e.stopPropagation()} role="presentation">
                      {readOnly
                        ? <span className="tw-text-sm tw-text-geist-fg-2">{doc.note || ""}</span>
                        : <UnderlineInput cell {...register(`documents_records.${rowIdx}.note`)} />}
                    </td>

                    <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap">
                      {!readOnly && (
                        <Clickable stopPropagation onClick={() => onRemove?.(rowIdx, doc)} title="Remove"
                          className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-geist-sm tw-text-geist-fg-3 hover:tw-bg-geist-100 hover:tw-text-geist-error">
                          <i className="bi bi-trash tw-text-[14px]" />
                        </Clickable>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

DocumentsTable.propTypes = {
  documents: PropTypes.arrayOf(PropTypes.shape({
    _idx: PropTypes.number,
    _key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    doc_platform_document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    source: PropTypes.string,
    inv_no: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inv_date: PropTypes.string,
    vendor_name: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    note: PropTypes.string,
  })),
  readOnly: PropTypes.bool,
  activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPreview: PropTypes.func,
  onRemove: PropTypes.func,
  onSearch: PropTypes.func,
  onErpSearch: PropTypes.func,
  register: PropTypes.func,
};

export default DocumentsTable;
